import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { getProfileLandingPath, isProfileComplete } from './lib/profile-rules'
import { useSession } from './lib/session'
import { useScrollMemory } from './lib/persist'
import { ApiModeBanner, TabBar, participantTabs, venueTabs } from './ui/Chrome'

import { LoginRoute } from './routes/Login'
import { getLoginConceptFromPath, LoginConceptsRoute } from './routes/LoginConcepts'
import { getLoginConceptV2FromPath, LoginConceptsV2Route } from './routes/LoginConceptsV2'
import { getLoginConceptV3FromPath, LoginConceptsV3Route } from './routes/LoginConceptsV3'
import { getMedicalConceptFromPath, MedicalLoginConceptRoute } from './routes/LoginConceptsV4'
import { ScheduleRoute } from './routes/Schedule'
import { ProgrammeRoute } from './routes/Programme'
import { CertificateRoute } from './routes/Certificate'
import { AccountRoute } from './routes/Account'
import { FeatureLandingRoute } from './routes/FeatureLanding'
import { AttendanceRoute } from './routes/Attendance'
import { VenueSettingsRoute } from './routes/VenueSettings'
import { AccommodationAdminRoute } from './routes/AccommodationAdmin'
import { AttendanceReportRoute } from './routes/AttendanceReport'
import { UserAdminRoute } from './routes/UserAdmin'
import { EventAdminRoute } from './routes/EventAdmin'
import { ManageRoute } from './routes/Manage'
import { useCheckinCount } from './features/useSlots'

/*
 * The scanner drags in a wasm barcode decoder, and the venue screen a QR renderer.
 * Neither belongs in the bundle that has to paint the programme on a phone at the
 * registration desk, so both are split out and fetched when first opened.
 */
const ScanRoute = lazy(() =>
  import('./routes/Scan').then((m) => ({ default: m.ScanRoute })),
)
const VenueDisplayRoute = lazy(() =>
  import('./routes/VenueDisplay').then((m) => ({ default: m.VenueDisplayRoute })),
)
const VenueCodeRoute = lazy(() =>
  import('./routes/VenueDisplay').then((m) => ({ default: m.VenueCodeRoute })),
)
/* The delegate pass: a QR renderer on one side, the wasm decoder on the other. */
const PassRoute = lazy(() =>
  import('./routes/Pass').then((m) => ({ default: m.PassRoute })),
)
const PassScanRoute = lazy(() =>
  import('./routes/PassScan').then((m) => ({ default: m.PassScanRoute })),
)
/* Certificate setup pulls in pdf.js; nobody else needs it. */
const IdeasRoute = lazy(() =>
  import('./routes/Ideas').then((m) => ({ default: m.IdeasRoute })),
)
/* The projector wall pulls in the QR renderer; the admin list does not. */
const IdeasWallRoute = lazy(() =>
  import('./routes/IdeasWall').then((m) => ({ default: m.IdeasWallRoute })),
)
const IdeasAdminRoute = lazy(() =>
  import('./routes/IdeasAdmin').then((m) => ({ default: m.IdeasAdminRoute })),
)
const AnnouncementsAdminRoute = lazy(() =>
  import('./routes/AnnouncementsAdmin').then((m) => ({ default: m.AnnouncementsAdminRoute })),
)
const CertificateAdminRoute = lazy(() =>
  import('./routes/CertificateAdmin').then((m) => ({ default: m.CertificateAdminRoute })),
)

/**
 * Routes that own the whole viewport and hide the app chrome. Note `/venue/show`
 * and not `/venue`: the venue picker keeps its tab bar, or staff land on a screen
 * with no way out of it.
 */
const IMMERSIVE = ['/scan', '/venue/show', '/staff/pass', '/venue/ideas']

/*
 * Design previews, lazily imported so their stylesheets - and the Inter face the v2
 * skin needs - never reach the bundle a delegate downloads. Same reason Scan and
 * VenueDisplay are split: nothing that only a few people open should be in the file
 * that paints the programme at the registration desk.
 */
const PreviewLoginRoute = lazy(() =>
  import('./routes/PreviewLogin').then((m) => ({ default: m.PreviewLoginRoute })))
const PreviewWipRoute = lazy(() =>
  import('./routes/PreviewWip').then((m) => ({ default: m.PreviewWipRoute })))

export function App() {
  const { session, ready, isStaff, isAdmin, isSuperAdmin } = useSession()
  const location = useLocation()
  /* Android discards background tabs; this puts each screen back where it was. */
  useScrollMemory(location.pathname)

  // Hold the first paint until localStorage has been read, or an authenticated
  // reload flashes the login screen before redirecting away from it.
  if (!ready) return <BootSplash />

  /*
   * The design-comparison clone, answered before anything else looks at the session.
   *
   * Registering it inside the route trees was not enough: signed out it worked, but
   * signed in the authenticated catch-all redirected it to '/', and a participant with
   * an incomplete profile would have been bounced by the profile gate as well. Since
   * the whole point is to hold it up against the real sign-in, it has to render the
   * same whether or not anyone is signed in.
   */
  if (location.pathname === '/preview/login') {
    return <Suspense fallback={<BootSplash />}><PreviewLoginRoute /></Suspense>
  }
  if (location.pathname === '/preview/wip') {
    return <Suspense fallback={<BootSplash />}><PreviewWipRoute /></Suspense>
  }

  const loginConcept = getLoginConceptFromPath(location.pathname)
  if (loginConcept) return <LoginConceptsRoute variant={loginConcept} />

  const loginConceptV2 = getLoginConceptV2FromPath(location.pathname)
  if (loginConceptV2) return <LoginConceptsV2Route variant={loginConceptV2} />

  const loginConceptV3 = getLoginConceptV3FromPath(location.pathname)
  if (loginConceptV3) return <LoginConceptsV3Route variant={loginConceptV3} />

  const medicalConcept = getMedicalConceptFromPath(location.pathname)
  if (medicalConcept) return <MedicalLoginConceptRoute variant={medicalConcept} />

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location }} />} />
      </Routes>
    )
  }

  // Incomplete participants land on Home, where the profile prompt is visible immediately.
  // Account remains available for later edits after the first confirmation.
  if (!isStaff) {
    const profilePath = getProfileLandingPath(location.pathname, isProfileComplete(session))
    if (profilePath !== location.pathname) {
      return <Navigate to={profilePath} replace />
    }
  }

  const immersive = IMMERSIVE.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  )

  return (
    <div className="app-shell" data-immersive={immersive || undefined}>
      {!immersive && <ApiModeBanner />}

      <div className="app-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          {isStaff ? (
          <>
            <Route path="/venue" element={<VenueDisplayRoute />} />
            <Route path="/venue/show/:slotId" element={<VenueCodeRoute />} />
            {/*
              * Organiser screens are not mounted for a venue display at all, rather
              * than mounted and refused by the API. A 403 arrives after the screen has
              * already painted its skeleton, so the delegate roster would flash as a
              * loading state before failing - and a route that exists invites a typed
              * URL. Absence is the cleaner gate; the server still enforces it.
              */}
            {isAdmin ? (
              <>
                <Route path="/admin" element={<ManageRoute />} />
                <Route path="/venue/settings" element={<VenueSettingsRoute />} />
                <Route path="/admin/accommodation" element={<AccommodationAdminRoute />} />
                <Route path="/admin/attendance" element={<AttendanceReportRoute />} />
                {/* No tab: seven is the ceiling at 375px. Reached from Settings. */}
                <Route path="/admin/event" element={<EventAdminRoute />} />
                <Route path="/admin/certificate" element={<CertificateAdminRoute />} />
                <Route path="/admin/ideas" element={<IdeasAdminRoute />} />
                <Route path="/admin/announcements" element={<AnnouncementsAdminRoute />} />
                <Route path="/venue/ideas" element={<IdeasWallRoute />} />
              </>
            ) : null}
            {/* Same reasoning as the organiser block: absence, not a 403 after paint. */}
            {isSuperAdmin ? (
              <Route path="/admin/users" element={<UserAdminRoute />} />
            ) : null}
            {/* Staff only, and the route tree is the gate: there is no participant path to it. */}
            <Route path="/staff/pass" element={<PassScanRoute />} />
            <Route path="/account" element={<AccountRoute />} />
            <Route path="*" element={<Navigate to="/venue" replace />} />
          </>
          ) : (
            <>
              <Route path="/" element={<ScheduleRoute />} />
              <Route path="/programme" element={<ProgrammeRoute />} />
              <Route path="/attendance" element={<AttendanceRoute />} />
              <Route path="/scan" element={<ScanRoute />} />
              <Route path="/scan/:slotId" element={<ScanRoute />} />
              <Route path="/certificate" element={<CertificateRoute />} />
              <Route path="/pass" element={<PassRoute />} />
              <Route path="/gallery" element={<FeatureLandingRoute kind="gallery" />} />
              <Route path="/resources" element={<FeatureLandingRoute kind="resources" />} />
              <Route path="/ideas" element={<IdeasRoute />} />
              <Route path="/account" element={<AccountRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
          </Routes>
        </Suspense>
      </div>

      {!immersive && <PrimaryTabs staff={isStaff} admin={isAdmin} superAdmin={isSuperAdmin} />}
    </div>
  )
}

function PrimaryTabs({ staff, admin, superAdmin }: { staff: boolean; admin: boolean; superAdmin: boolean }) {
  const count = useCheckinCount()
  return <TabBar items={staff ? venueTabs(admin, superAdmin) : participantTabs(count)} />
}

function RouteFallback() {
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', minHeight: '50svh' }}>
      <span className="kicker faint">Loading</span>
    </div>
  )
}

function BootSplash() {
  return (
    <div
      style={{
        minHeight: '100svh',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--ink-faint)',
      }}
    >
      <span className="kicker">CPD</span>
    </div>
  )
}
